/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR055: Hook for managing merchant sources
 * CR103: Added category enforcement for new/uncategorized merchants
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { normalizeSourceType, type CanonicalSourceType } from "@/lib/sourceTypes";
import { type CategoryKey } from "@/lib/config/categories";

export type SourceStatus = "active" | "paused" | "deprecated";

// Re-export CanonicalSourceType for backwards compatibility
export type SourceType = CanonicalSourceType;

// CR308: Scan type enum - determines which pipeline processes this source
export type ScanType = "WEB" | "FLYER";

export interface MerchantSource {
  id: string;
  merchant_id: string;
  merchant_name: string | null;
  source_url: string;
  source_label: string;
  source_type: string | null;
  scan_type: ScanType;  // CR308: WEB or FLYER - determines pipeline
  priority: number;
  is_active: boolean;
  status: SourceStatus;
  notes: string | null;
  web_recipe_template_id: string | null; // CR308-A: per-source web recipe override
  created_at: string;
  updated_at: string;
}

export interface SourceHealthStats {
  source_id: string;
  success_count: number;
  failure_count: number;
  failure_rate: number;
  avg_text_length: number;
  avg_quality_score: number;
  last_checked_at: string | null;
}

export interface MerchantSourcesSummary {
  merchant_id: string;
  merchant_name: string;
  active_sources: number;
  total_sources: number;
  category?: string | null;
}

export function useMerchantSources() {
  const [sources, setSources] = useState<MerchantSource[]>([]);
  const [merchantCategories, setMerchantCategories] = useState<Map<string, string | null>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchSources = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch sources and merchant categories in parallel
      const [sourcesResult, merchantsResult] = await Promise.all([
        supabase
          .from("merchant_sources")
          .select("*")
          .order("merchant_name", { ascending: true })
          .order("priority", { ascending: true }),
        supabase
          .from("merchants")
          .select("merchant_key, category"),
      ]);

      if (sourcesResult.error) throw sourcesResult.error;
      
      // Map database records to our interface with proper status handling
      const mappedSources: MerchantSource[] = (sourcesResult.data || []).map((s: any) => ({
        ...s,
        status: s.status || (s.is_active ? "active" : "paused"),
      }));
      
      setSources(mappedSources);

      // CR103: Store merchant categories for lookup
      const categoryMap = new Map<string, string | null>();
      (merchantsResult.data || []).forEach((m: any) => {
        categoryMap.set(m.merchant_key, m.category);
      });
      setMerchantCategories(categoryMap);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch sources";
      setError(message);
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  // CR103: Fetch merchant categories for summary
  const fetchMerchantCategories = async (): Promise<Map<string, string | null>> => {
    const { data } = await supabase
      .from("merchants")
      .select("merchant_key, category");
    const categoryMap = new Map<string, string | null>();
    (data || []).forEach((m: any) => {
      categoryMap.set(m.merchant_key, m.category);
    });
    return categoryMap;
  };

  /**
   * CR104: Add source with optional category for new/uncategorized merchants
   * @param source - Source data to insert
   * @param merchantCategory - Category to set if merchant is new or missing category
   * @param merchantDisplayName - Display name for new merchants (overrides source.merchant_name)
   */
  const addSource = async (
    source: Omit<MerchantSource, "id" | "created_at" | "updated_at">,
    merchantCategory?: CategoryKey | null,
    merchantDisplayName?: string | null
  ) => {
    // Normalize source type to canonical form
    const normalizedType = normalizeSourceType(source.source_type);
    
    // Check for duplicate active source with same type for this merchant
    const existingDupe = sources.find(
      (s) => 
        s.merchant_id === source.merchant_id &&
        normalizeSourceType(s.source_type) === normalizedType &&
        s.status === "active"
    );
    
    if (existingDupe) {
      toast({
        title: "Duplicate Source Type",
        description: `This merchant already has an active "${normalizedType}" source. Please deactivate the existing one first or use a different type.`,
        variant: "destructive",
      });
      throw new Error(`Duplicate active source type: ${normalizedType}`);
    }

    // CR103: Check if merchant exists and handle category
    const { data: existingMerchant } = await supabase
      .from("merchants")
      .select("merchant_key, category")
      .eq("merchant_key", source.merchant_id)
      .maybeSingle();

    const isNewMerchant = !existingMerchant;
    const merchantMissingCategory = existingMerchant && !existingMerchant.category;

    // CR104/CR403/CR396: Create or update merchant with category if needed
    if (isNewMerchant && merchantCategory) {
      // Create new merchant with category (use display name if provided)
      const displayName = merchantDisplayName || source.merchant_name || source.merchant_id;
      
      // CR403: merchantCategory should be the category_id UUID
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(merchantCategory);
      
      // CR396: Return the created merchant so we can capture its UUID
      const { data: newMerchant, error: merchantError } = await supabase
        .from("merchants")
        .insert({
          merchant_key: source.merchant_id,
          display_name: displayName,
          category: merchantCategory,
          category_id: isUuid ? merchantCategory : "727259d5-8769-4d5f-a07e-53803a9cbeec",
        })
        .select("id")
        .single();
      
      if (merchantError) {
        console.error("[useMerchantSources] Failed to create merchant:", merchantError);
        throw new Error(`Failed to create merchant: ${merchantError.message}`);
      }
      
      // CR396: Attach the newly created UUID to the source for insert
      if (newMerchant?.id) {
        (source as any).merchant_uuid = newMerchant.id;
      }
    } else if (merchantMissingCategory && merchantCategory) {
      // Update existing merchant with category
      const { error: updateError } = await supabase
        .from("merchants")
        .update({ category: merchantCategory })
        .eq("merchant_key", source.merchant_id);
      
      if (updateError) {
        console.error("[useMerchantSources] Failed to update merchant category:", updateError);
        throw new Error(`Failed to update merchant category: ${updateError.message}`);
      }
    }
    
    try {
      // CR308: Determine scan_type - official_flyer forces FLYER, otherwise default to WEB
      const scanType: ScanType = normalizedType === "official_flyer" ? "FLYER" : (source.scan_type || "WEB");
      
      // CR396: Resolve merchant_uuid — either from the source payload or look it up
      let merchantUuid = (source as any).merchant_uuid || null;
      if (!merchantUuid) {
        const { data: mRow } = await supabase
          .from("merchants")
          .select("id")
          .eq("merchant_key", source.merchant_id)
          .maybeSingle();
        merchantUuid = mRow?.id || null;
      }
      
      const { data, error: insertError } = await supabase
        .from("merchant_sources")
        .insert({
          merchant_id: source.merchant_id,
          merchant_uuid: merchantUuid,
          merchant_name: source.merchant_name,
          source_url: source.source_url,
          source_label: source.source_label,
          source_type: normalizedType, // Store canonical form
          scan_type: scanType,  // CR308: Include scan_type
          priority: source.priority,
          is_active: source.status === "active",
          status: source.status,
          notes: source.notes,
        })
        .select()
        .single();

      if (insertError) throw insertError;
      
      await fetchSources();
      toast({ title: "Success", description: "Source added successfully" });
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to add source";
      if (!message.includes("Duplicate")) {
        toast({ title: "Error", description: message, variant: "destructive" });
      }
      throw err;
    }
  };

  const updateSource = async (id: string, updates: Partial<MerchantSource>) => {
    try {
      const updatePayload: any = { ...updates };
      
      // Normalize source_type if it's being updated
      if (updates.source_type) {
        updatePayload.source_type = normalizeSourceType(updates.source_type);
        
        // CR308: If changing to official_flyer, force scan_type to FLYER
        if (normalizeSourceType(updates.source_type) === "official_flyer") {
          updatePayload.scan_type = "FLYER";
        }
      }
      
      // Sync is_active with status
      if (updates.status) {
        updatePayload.is_active = updates.status === "active";
      }
      
      const { error: updateError } = await supabase
        .from("merchant_sources")
        .update(updatePayload)
        .eq("id", id);

      if (updateError) throw updateError;
      
      await fetchSources();
      toast({ title: "Success", description: "Source updated successfully" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update source";
      toast({ title: "Error", description: message, variant: "destructive" });
      throw err;
    }
  };

  const deleteSource = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from("merchant_sources")
        .delete()
        .eq("id", id);

      if (deleteError) throw deleteError;
      
      await fetchSources();
      toast({ title: "Success", description: "Source deleted successfully" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete source";
      toast({ title: "Error", description: message, variant: "destructive" });
      throw err;
    }
  };

  const toggleStatus = async (id: string, currentStatus: SourceStatus) => {
    const newStatus: SourceStatus = currentStatus === "active" ? "paused" : "active";
    await updateSource(id, { status: newStatus });
  };

  const deprecateSource = async (id: string) => {
    await updateSource(id, { status: "deprecated" });
  };

  const updatePriority = async (id: string, priority: number) => {
    await updateSource(id, { priority });
  };

  // Get unique merchants from sources with category data
  const getMerchantsSummary = (): MerchantSourcesSummary[] => {
    const merchantMap = new Map<string, MerchantSourcesSummary>();
    
    sources.forEach((source) => {
      const existing = merchantMap.get(source.merchant_id);
      if (existing) {
        existing.total_sources++;
        if (source.status === "active") {
          existing.active_sources++;
        }
      } else {
        merchantMap.set(source.merchant_id, {
          merchant_id: source.merchant_id,
          merchant_name: source.merchant_name || source.merchant_id,
          total_sources: 1,
          active_sources: source.status === "active" ? 1 : 0,
          // CR103: Include category from merchants table
          category: merchantCategories.get(source.merchant_id) || null,
        });
      }
    });
    
    return Array.from(merchantMap.values()).sort((a, b) => 
      a.merchant_name.localeCompare(b.merchant_name)
    );
  };

  /**
   * CR103: Update merchant category
   */
  const updateMerchantCategory = async (merchantId: string, category: CategoryKey) => {
    try {
      const { error: updateError } = await supabase
        .from("merchants")
        .update({ category })
        .eq("merchant_key", merchantId);

      if (updateError) throw updateError;

      // Update local cache
      setMerchantCategories((prev) => new Map(prev).set(merchantId, category));
      toast({ title: "Success", description: "Merchant category updated" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update category";
      toast({ title: "Error", description: message, variant: "destructive" });
      throw err;
    }
  };

  /**
   * CR103: Get category for a specific merchant
   */
  const getMerchantCategory = (merchantId: string): string | null => {
    return merchantCategories.get(merchantId) || null;
  };

  return {
    sources,
    loading,
    error,
    fetchSources,
    addSource,
    updateSource,
    deleteSource,
    toggleStatus,
    deprecateSource,
    updatePriority,
    getMerchantsSummary,
    updateMerchantCategory,
    getMerchantCategory,
  };
}
